/**
 * A KÉZI SZINKRON VISSZAJELZÉSE — egy sor, a felületek fölött.
 *
 * MIÉRT A VÁZBAN, ÉS NEM A HIÁNYLISTÁN. A művelet a palettáról indul, az
 * pedig MINDKÉT felületen elérhető (`Ctrl+K`). Ha a visszajelzés a hiánylista
 * belsejében élne, a munkalistáról indított szinkron NÉMÁN futna le — a
 * könyvelő megnyomná, és semmit nem látna belőle.
 *
 * ⚠️ A SZÁMOT MONDJUK KI, NEM AZT, HOGY „KÉSZ". A `202` azt nyugtázza, hogy a
 * cégek a soron vannak; a lekérdezés percekkel később fut le. Egy „kész"
 * felirat ott azt állítaná, hogy a lista már friss — és a könyvelő egy
 * változatlan képernyőre nézve azt hinné, nincs új hiány. Ez a 2.4d „hamis
 * nulla" bukásmódja, csak a másik irányból.
 *
 * ⚠️ ÉS EZÉRT NEM MONDUNK KÉSZET ELŐBB (#37). A sáv mostantól a VALÓDI
 * állapotot mutatja (`GET /nav/sync/status`), de a „kész" szó továbbra is
 * csak a `done` ágon szerepel — amikor a kör tényleg lefutott. A `running`
 * cégeket számol, nem ígér; a `stalled` pedig kimondja, hogy a munka megy
 * tovább, csak mi nem várunk rá.
 *
 * A KIMARADT CÉGEKET IS KIMONDJUK, ugyanazzal az érveléssel, mint a bekérésnél
 * (`DiscrepancyList.tsx` `RequestFeedback`): egy „3 cég sorba téve" felirat
 * nyolcvanból csendben veszítene el hetvenhetet.
 */

import { useTranslation } from 'react-i18next';
import type { NavSyncState } from './useNavSync';

export function NavSyncNotice({ sync }: { sync: NavSyncState }) {
  const { t } = useTranslation();

  switch (sync.status) {
    case 'idle':
      return null;

    case 'sending':
      // `<output>`, nem `<p role="status">`: ugyanaz az élő régió, de a
      // szerep a MEGFELELŐ ELEMBŐL jön, nem egy rátett attribútumból (ez a
      // váz konvenciója, és a Biome is ezt kéri). Jelzés nélkül a
      // billentyűzet-első felületen (D9) a képernyőolvasót használó könyvelő
      // nem tudná meg, hogy a palettáról indított művelet elindult.
      return <output className="hint">{t('navSync.sending')}</output>;

    case 'running':
      return <RunningNotice sync={sync} />;

    case 'done':
      return <DoneNotice sync={sync} />;

    case 'stalled':
      // ⚠️ NEM HIBA, ÉS NEM IS „KÉSZ". A munka a soron halad tovább; mi
      // hagytuk abba a nézését. Egy „nem sikerült" felirat itt arra vinné a
      // könyvelőt, hogy újraindítsa azt, ami épp fut.
      return (
        <output className="notice">
          <p>{t('navSync.stalled')}</p>
          <button type="button" onClick={sync.dismiss}>
            {t('navSync.dismiss')}
          </button>
        </output>
      );

    case 'forbidden':
      // AZ ÚJRAPRÓBÁLÁS ITT NEM SEGÍT — a szerepkör a friss adatbázisból
      // dolgozik, és magától nem változik vissza. Emberhez kell fordulni.
      return (
        <p className="notice" role="alert">
          {t('navSync.forbidden')}
        </p>
      );

    case 'failed':
      return (
        <p className="notice" role="alert">
          {t('navSync.failed')}
        </p>
      );

    default: {
      const exhaustive: never = sync.status;
      throw new Error(`Ismeretlen szinkron-állapot: ${JSON.stringify(exhaustive)}`);
    }
  }
}

/**
 * A FUTÓ KÖR — a valódi folyamatjelzés (#37).
 *
 * Három réteg, csökkenő fontossággal: HÁNY CÉG van meg, MENNYI IDŐ van hátra,
 * és mennyi lap jött be. A harmadik életjel: egy lassú cégnél a cégszám
 * percekig nem mozdul, a lapszám viszont igen — enélkül a képernyő úgy nézne
 * ki, mintha megállt volna a munka.
 */
function RunningNotice({ sync }: { sync: NavSyncState }) {
  const { t } = useTranslation();
  const queued = sync.result?.queued ?? 0;
  const progress = sync.progress;
  const notConnected = (sync.result?.notConnected ?? 0) + (progress?.notConnected ?? 0);
  // A BEKÖTETLEN CÉG IS ELINTÉZETT (#38): a kör rajta VÉGET ÉRT, csak nem
  // sikerrel. A `failed`-del egy sorban számít — különben a sáv megállna egy
  // olyan körnél, aminek a fele bekötetlen cégre esett.
  const done = (progress?.finished ?? 0) + (progress?.failed ?? 0) + (progress?.notConnected ?? 0);
  // ⚠️ A MONDAT AZ ÖSSZEGET KAPJA, A SZÁMLÁLÓ NEM (code review, #38). Enélkül
  // a kimaradt cégek száma UGRANA a kör végén — menet közben „2 cég kimaradt",
  // a végén „7" —, és a könyvelő azt hinné, közben romlott valami. A `done`
  // viszont a NEVEZŐHÖZ mér, abban pedig a sorba tételnél kihagyott cégek
  // nincsenek benne: beszámítva a sáv túlcsordulna.

  return (
    <output className="notice">
      <p>{t('navSync.progress', { done, total: queued })}</p>

      {/* ⚠️ `aria-hidden`: a sáv UGYANAZT mondja, amit a fölötte álló mondat.
          Élő régióban egy külön bejelentett `progressbar` minden körben
          megszólalna, és a képernyőolvasó a szöveget is, a sávot is
          felolvasná — háromszor annyi hang, nulla többletinformációért. */}
      <div className="progress" aria-hidden="true">
        <div className="progress__fill" style={{ width: `${percentOf(done, queued)}%` }} />
      </div>

      <p className="hint">{etaLabel(t, sync.etaSeconds)}</p>

      {progress !== null && progress.pagesFetched > 0 ? (
        <p className="hint">{t('navSync.pages', { count: progress.pagesFetched })}</p>
      ) : null}

      {/* A BUKOTT CÉGEKET MENET KÖZBEN IS KIMONDJUK. Elhallgatva a végén
          derülne ki, hogy a kör fele nem hozott adatot — és addigra a
          könyvelő már friss listának hiszi, amit lát. */}
      {progress !== null && progress.failed > 0 ? (
        <p>{t('navSync.runFailed', { count: progress.failed })}</p>
      ) : null}

      {/* ÉS A BEKÖTETLENEKET IS (#38) — külön mondattal. A `runFailed` azt
          ígéri, hogy „a napi kör újrapróbálja"; bekötetlen cégre az hazugság,
          mert a napi kör ugyanígy meg fog bukni rajtuk, amíg valaki létre nem
          hozza a technikai felhasználót a NAV felületén. */}
      {notConnected > 0 ? <p>{t('navSync.notConnected', { count: notConnected })}</p> : null}
    </output>
  );
}

/** A LEFUTOTT KÖR. Itt már szabad „kész"-t mondani — és a lista is frissült. */
function DoneNotice({ sync }: { sync: NavSyncState }) {
  const { t } = useTranslation();
  const result = sync.result;
  const progress = sync.progress;
  // ⚠️ A BEKÖTETLENEK KÉT FORRÁSBÓL JÖNNEK, ÉS ÖSSZEADÓDNAK (#38). A
  // `result.notConnected` a SORBA TÉTELÉ: a cég nem volt bekötve, el sem
  // indult. A `progress.notConnected` azoké, amik átmentek a szűrőn, de
  // futásidőben nem volt kredencia-soruk. A két halmaz
  // diszjunkt, és a könyvelő teendője azonos: be kell kötni a céget. Két
  // mondatban ugyanaz a kérés kétszer szólna, két különböző számmal.
  const notConnected = (result?.notConnected ?? 0) + (progress?.notConnected ?? 0);
  const label = doneLabel(t, {
    queued: result?.queued ?? 0,
    finished: progress?.finished ?? 0,
    failed: progress?.failed ?? 0,
    // ⚠️ CSAK A FUTÁSIDEJŰ SZÁM, NEM AZ ÖSSZEG — a kettő nem egyforma súlyú
    // a ZÁRÓ MONDAT szempontjából (code review, #38). A futásidejű cégek
    // BENNE voltak a nevezőben: meg akartuk nézni a NAV-ot, és nem sikerült,
    // tehát nem állíthatjuk, hogy „nem volt új adat". A sorba tételnél
    // kihagyottak viszont soha nem is voltak a körben — a kör nélkülük futott
    // végig, és hiánytalanul futott végig. Összemosva egy teljesen rendben
    // lefutott kör lezárását semmi nem mondaná ki.
    //
    // A `notConnected` MONDATA viszont az összeget kapja: ott a könyvelő
    // teendője a kérdés, és az mindkét halmaznál ugyanaz.
    notConnected: progress?.notConnected ?? 0,
    upToDate: progress?.upToDate ?? 0,
    createdRows: progress?.createdRows ?? 0,
  });

  return (
    <output className="notice notice--clear">
      {/* NULLA BEKÖTÖTT CÉG SAJÁT MONDATOT KAP. A „0 cég sorba téve"
          nyelvtanilag helyes és tartalmilag használhatatlan: nem mondja
          meg, hogy a NAV bekötése hiányzik, nem a gomb romlott el. */}
      {label !== null ? <p>{label}</p> : null}

      {progress !== null && progress.failed > 0 ? (
        <p>{t('navSync.runFailed', { count: progress.failed })}</p>
      ) : null}

      {notConnected > 0 ? <p>{t('navSync.notConnected', { count: notConnected })}</p> : null}

      {/* RÉSZLEGES SIKER A SORBA TÉTELNÉL. Elhallgatva a szám azt állítaná,
          hogy minden cég elindult — és a könyvelő nem tudná, kire kell
          visszanéznie. Ez NEM ugyanaz, mint a `runFailed`: ott a lekérdezés
          bukott, itt a sorba tevés maga. */}
      {result && result.failed > 0 ? (
        <p>{t('navSync.partlyFailed', { count: result.failed })}</p>
      ) : null}

      <button type="button" onClick={sync.dismiss}>
        {t('navSync.dismiss')}
      </button>
    </output>
  );
}

/** A sáv kitöltése százalékban — nevező nélkül nulla, nem `NaN`. */
function percentOf(done: number, total: number): number {
  if (total <= 0) {
    return 0;
  }
  return Math.min(100, Math.round((done / total) * 100));
}

/**
 * A HÁTRALÉVŐ IDŐ — HÁROM MONDAT, MIND LITERÁL KULCCSAL.
 *
 * ⚠️ A KULCSOK NEM ÖSSZEFŰZVE ÁLLNAK, a `statusLabel.ts` szabálya szerint:
 * így az i18n-kapu statikusan mind a hármat látja. Egy hiányzó fordítás itt
 * nyers kulcsként jelenne meg a könyvelő képernyőjén.
 *
 * ⚠️ ÉS AZ ISMERETLEN IDŐ IS MONDAT, nem elhagyott sor. Az első körben nincs
 * mire alapozni a becslést (`medianRunSeconds === null`); egy üres hely ott
 * azt sugallná, hogy elromlott valami. Kitalált szám viszont rosszabb lenne
 * mindkettőnél: azt a könyvelő EL IS HINNÉ.
 */
function etaLabel(
  t: (key: string, options?: Record<string, unknown>) => string,
  eta: number | null
) {
  if (eta === null) {
    return t('navSync.etaUnknown');
  }
  if (eta < 60) {
    return t('navSync.etaSoon');
  }
  return t('navSync.eta', { count: Math.ceil(eta / 60) });
}

/**
 * A ZÁRÓ MONDAT — és a NÉGY eset tényleg négy különböző dolgot jelent.
 *
 * `queued === 0`: nincs bekötött cég.
 *
 * ⚠️ `finished === 0` ÉS VOLT BUKÁS: NINCS záró mondat, csak a hibasor. A
 * „nem volt új NAV-adat" itt HAZUDNA — nem azért nincs adat, mert nem volt,
 * hanem mert a lekérdezés nem jutott el odáig. A két mondat egymás alatt
 * (`nem volt új adat` + `3 cégnél hibára futott`) pont az a 2.4d
 * bizalomvesztés, ami ellen az egész tétel szól. *(Viktor, 09-15.)*
 *
 * `finished === 0`, bukás nélkül, de `upToDate > 0`: a munkás VÉGIGMENT a
 * cégeken, és mindegyiknél megvolt már a mai adat. Ez a „nem volt új
 * NAV-adat" egyik igaz esete — és az `upToDate` az, ami bizonyítja. Nélküle
 * ugyanez az állapot attól is jöhetne, hogy a kör el sem indult
 * (`nav-sync-progress.ts`).
 *
 * ⚠️ `finished > 0`, DE `createdRows === 0`: A NEGYEDIK ESET, és ez sokáig
 * észrevétlen volt. A kör LEFUTOTT, lekérdezte a lapot, és nulla új sort
 * hozott — mire a felület azt mondta, hogy *„{{count}} cég NAV-adata frissült.
 * A lista is frissült."* Egyik fele sem igaz: nem frissült semmi, és a
 * listának nem volt mit mutatnia. Ugyanaz a bizalomvesztés, ami ellen a
 * `runFailed` menet közbeni kimondása véd — a könyvelő friss listának hiszi,
 * amit lát. *(#37, élesben mérve 2026-09-17.)*
 *
 * ⚠️ MIÉRT MOST DERÜLT KI: a #22 javítása előtt az üres lap HIBÁRA FUTOTT,
 * tehát a `finished` nem is nőtt — ez az ág nem volt elérhető. Az üres kör
 * azóta helyesen sikeres, és ettől lett ez a mindennapi eset.
 *
 * Az ötödik a rendes eset: futott is, hozott is.
 */
function doneLabel(
  t: (key: string, options?: Record<string, unknown>) => string,
  round: {
    queued: number;
    finished: number;
    failed: number;
    notConnected: number;
    upToDate: number;
    createdRows: number;
  }
): string | null {
  if (round.queued === 0) {
    return t('navSync.none');
  }
  // ⚠️ A `notConnected` ITT A `failed`-DEL AZONOSAN VISELKEDIK (#38): ha van
  // ilyen cég, NINCS záró mondat, mert a saját mondatuk mondja el, mi történt.
  // A *„Egyik cégnél sem volt új NAV-adat"* fölöttük azt állítaná, hogy a kör
  // rendben végigment — holott a cégek fele meg sem nézte a NAV-ot.
  if (round.finished === 0) {
    return round.failed === 0 && round.notConnected === 0 && round.upToDate > 0
      ? t('navSync.doneNothingNew')
      : null;
  }
  // LEFUTOTT, DE NEM HOZOTT SEMMIT. A `finished` itt NEM használható: az
  // befejezettséget mér, nem eredményt.
  if (round.createdRows === 0) {
    return round.failed === 0 && round.notConnected === 0 ? t('navSync.doneNothingNew') : null;
  }
  return t('navSync.done', { count: round.finished });
}
